import type { Path } from "router";

const SETTINGS_CLUB_SUFFIXES = ["", "/public-directory", "/documents", "/players"] as const;

export type ClubSwitchNavigation =
  | { kind: "settings"; path: Path; params: { clubId: string } }
  | { kind: "club-home"; path: "/clubs/:clubId"; params: { clubId: string } }
  | { kind: "none" };

/**
 * Where to navigate when the user switches the current club.
 * Preserves the active club settings sub-page when already in club settings.
 * Account, profile, and desktop settings are not club-scoped, so stay put.
 */
export function clubSwitchTargetPathname(pathname: string, newClubId: string): string | null {
  const destination = resolveClubSwitchNavigation(pathname, newClubId);
  if (destination.kind === "none") {
    return null;
  }
  if (destination.kind === "club-home") {
    return `/clubs/${newClubId}`;
  }
  const suffix = destination.path.replace("/settings/clubs/:clubId", "");
  return `/settings/clubs/${newClubId}${suffix}`;
}

export function resolveClubSwitchNavigation(pathname: string, newClubId: string): ClubSwitchNavigation {
  const settingsMatch = pathname.match(/^\/settings\/clubs\/[^/]+(\/[^/?#]*)?/);
  if (settingsMatch) {
    const suffix = settingsMatch[1] ?? "";
    const normalizedSuffix = SETTINGS_CLUB_SUFFIXES.includes(
      suffix as (typeof SETTINGS_CLUB_SUFFIXES)[number],
    )
      ? suffix
      : "";
    const path = `/settings/clubs/:clubId${normalizedSuffix}` as Path;
    return { kind: "settings", path, params: { clubId: newClubId } };
  }

  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    return { kind: "none" };
  }

  if (pathname === `/clubs/${newClubId}` || pathname.startsWith(`/clubs/${newClubId}/`)) {
    return { kind: "none" };
  }

  return { kind: "club-home", path: "/clubs/:clubId", params: { clubId: newClubId } };
}
